import { APP_NAME } from '@/constants';
import { cn } from '@/lib/utils';

/**
 * App mark (`public/logo-mark.svg`): the play arrow and its rings, without
 * the wordmark. Decorative: the app name is always written next to it.
 */
export function AppLogo({ className }: { className?: string }) {
	return (
		<img
			src="/logo-mark.svg"
			alt=""
			aria-hidden="true"
			width={32}
			height={32}
			decoding="async"
			draggable={false}
			className={cn('size-8 shrink-0 select-none', className)}
		/>
	);
}

/**
 * Full logo (`public/logo.svg`): the mark above the "DiGi Ads" wordmark.
 * Inline so "DiGi" follows `currentColor` (the logo's gray by default, set
 * a text color on dark surfaces); size it by height.
 */
export function AppLogoFull({ className }: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 1374.6667 1257.172"
			role="img"
			aria-label={APP_NAME}
			className={cn('text-muted-foreground h-20 w-auto shrink-0', className)}
		>
			<path
				fill="#ffe173"
				transform="matrix(1.3333333,0,0,-1.3333333,735.69667,255.55093)"
				d="m 0,0 -11.498,-42.913 -28.975,-108.135 c 1.406,-0.051 2.782,-0.212 4.201,-0.212 63.503,0 114.985,51.481 114.985,114.986 0,63.506 -51.482,114.986 -114.985,114.986 -63.506,0 -114.989,-51.48 -114.989,-114.986 0,-1.418 0.162,-2.794 0.213,-4.199 l 108.136,28.975 z"
			/>
			<path
				fill="#ffe173"
				transform="matrix(1.3333333,0,0,-1.3333333,687.332,-5.3333333e-4)"
				d="m 0,0 c -125.887,0 -227.937,-102.051 -227.937,-227.938 0,-11.508 1.128,-22.727 2.774,-33.777 l 49.806,13.345 c -0.779,6.716 -1.282,13.512 -1.282,20.432 0,97.399 79.24,176.639 176.639,176.639 97.398,0 176.638,-79.24 176.638,-176.639 0,-97.398 -79.24,-176.638 -176.638,-176.638 -6.921,0 -13.718,0.503 -20.433,1.281 L -33.777,-453.1 c 11.049,-1.646 22.268,-2.775 33.777,-2.775 125.887,0 227.937,102.051 227.937,227.937 C 227.937,-102.051 125.887,0 0,0"
			/>
			<path
				fill="#fbb040"
				transform="matrix(1.3333333,0,0,-1.3333333,539.38027,343.56)"
				d="M 0,0 -59.473,-15.936 -109,-29.206 -273.662,-73.327 7.903,-354.893 l 44.121,164.662 13.272,49.527 15.935,59.473 29.733,110.963 z"
			/>
			<path
				fill="#ffe173"
				transform="matrix(1.3333333,0,0,-1.3333333,687.332,303.91613)"
				d="m 0,0 -350.337,-137.349 -34.289,34.289 z"
			/>
			<path
				fill="currentColor"
				transform="matrix(1.3333333,0,0,-1.3333333,144.25907,1021.5887)"
				d="m 0,0 -53.037,0.266 c -7.778,-0.179 -11.667,-4.067 -11.667,-11.668 v -107.136 c 0,-7.954 4.243,-11.845 12.728,-11.667 L 0,-130.471 c 25.369,-0.354 44.197,9.368 56.484,29.172 6.1,9.812 9.15,21.655 9.15,35.532 0,20.598 -6.72,36.864 -20.154,48.796 C 33.104,-6.013 17.945,-0.354 0,0 m 0,43.489 c 33.061,-0.44 59.488,-10.561 79.291,-30.362 19.799,-19.8 29.7,-46.099 29.7,-78.894 0,-32.615 -9.945,-58.871 -29.833,-78.757 -19.889,-19.89 -46.141,-29.834 -78.76,-29.834 l -92.681,0.663 c -10.609,-0.179 -15.911,6.185 -15.911,19.092 V 26.785 c 0,11.137 5.302,16.792 15.911,16.971 z"
			/>
			<path
				fill="currentColor"
				transform="matrix(1.3333333,0,0,-1.3333333,319.14067,1108.6551)"
				d="M 0,0 C 0,14.407 7.381,21.745 22.144,22.01 36.904,22.187 44.021,14.938 43.49,0.266 v -87.314 c 0.531,-14.675 -6.586,-21.925 -21.346,-21.745 C 7.381,-108.528 0,-101.191 0,-86.783 Z"
			/>
			<path
				fill="currentColor"
				transform="matrix(1.3333333,0,0,-1.3333333,725.8672,1108.6551)"
				d="M 0,0 C 0,14.407 7.381,21.745 22.145,22.01 36.904,22.187 44.021,14.938 43.49,0.266 v -87.314 c 0.531,-14.675 -6.586,-21.925 -21.345,-21.745 C 7.381,-108.528 0,-101.191 0,-86.783 Z"
			/>
			<path
				fill="currentColor"
				transform="matrix(1.3333333,0,0,-1.3333333,637.25787,1107.0392)"
				d="M 0,0 C 0,14.408 7.381,21.745 22.144,22.01 36.906,22.187 44.286,15.026 44.286,0.531 l -0.793,-94.672 c 0,-10.075 -4.597,-15.028 -13.791,-14.85 l -94.009,-0.663 c -32.529,0 -58.758,9.966 -78.692,29.9 -19.936,19.933 -29.899,46.163 -29.899,78.692 0,32.62 9.944,58.872 29.832,78.761 19.889,19.889 45.833,30.054 77.831,30.495 l 90.163,0.266 c 12.552,0.176 18.828,-6.981 18.828,-21.48 0,-14.497 -6.1,-21.658 -18.298,-21.48 L 14.853,64.97 -64.704,64.705 c -19.094,-0.354 -34.653,-6.652 -46.673,-18.893 -12.021,-12.245 -18.032,-27.869 -18.032,-46.874 0,-27.492 11.137,-46.761 33.413,-57.809 9.899,-4.95 20.464,-7.426 31.689,-7.426 l 53.702,0.797 C -3.534,-66.031 0,-62.849 0,-55.954 Z"
			/>
			<path
				fill="currentColor"
				transform="matrix(1.3333333,0,0,-1.3333333,376.22267,1033.9468)"
				d="m 0,0 c 0,-11.626 -9.426,-21.052 -21.052,-21.052 -11.627,0 -21.053,9.426 -21.053,21.052 0,11.627 9.426,21.052 21.053,21.052 C -9.426,21.052 0,11.627 0,0"
			/>
			<path
				fill="currentColor"
				transform="matrix(1.3333333,0,0,-1.3333333,782.9492,1033.9468)"
				d="m 0,0 c 0,-11.626 -9.426,-21.052 -21.053,-21.052 -11.627,0 -21.051,9.426 -21.051,21.052 0,11.627 9.424,21.052 21.051,21.052 C -9.426,21.052 0,11.627 0,0"
			/>
			<path
				fill="#fbb040"
				transform="matrix(1.3333333,0,0,-1.3333333,967.92187,1120.2253)"
				d="M 0,0 -27.65,76.069 -55.3,0 Z m 6.59,-18.187 h -68.62 l -29.372,-80.797 h -19.339 L -27.65,129.077 55.291,-98.984 H 35.952 Z"
			/>
			<path
				fill="#fbb040"
				transform="matrix(1.3333333,0,0,-1.3333333,1216.4167,1171.6069)"
				d="m 0,0 v 22.783 c -2.013,10.792 -7.17,20.34 -15.473,28.652 -11.082,11.072 -24.448,16.614 -40.118,16.614 -15.659,0 -29.025,-5.542 -40.108,-16.614 -11.072,-11.083 -16.614,-24.451 -16.614,-40.109 0,-15.669 5.542,-28.989 16.614,-39.969 11.083,-10.988 24.449,-16.473 40.108,-16.473 15.67,0 29.036,5.485 40.118,16.473 C -7.17,-20.246 -2.013,-10.689 0,0 M 18.187,-60.448 H 0 v 19.909 c -16.146,-15.753 -34.67,-23.634 -55.591,-23.634 -20.816,0 -38.6,7.375 -53.352,22.136 -14.762,14.752 -22.137,32.537 -22.137,53.363 0,20.91 7.375,38.741 22.137,53.503 14.752,14.752 32.536,22.128 53.352,22.128 20.921,0 39.445,-7.873 55.591,-23.636 v 93.407 h 18.187 z"
			/>
			<path
				fill="#fbb040"
				transform="matrix(1.3333333,0,0,-1.3333333,1319.6628,1162.8204)"
				d="m 0,0 c -10.222,5.541 -17.382,10.36 -21.491,14.471 -6.683,6.589 -10.025,14.611 -10.025,24.065 0,9.453 3.342,17.55 10.025,24.279 6.683,6.741 14.705,10.101 24.065,10.101 9.454,0 17.335,-3.145 23.635,-9.454 6.02,-6.112 9.023,-13.132 9.023,-21.051 v -2.724 l -16.044,-6.45 c -1.141,8.687 -3.079,14.425 -5.792,17.196 -2.725,2.76 -6.329,4.146 -10.822,4.146 -4.388,0 -8.162,-1.544 -11.316,-4.652 -3.154,-3.108 -4.728,-6.899 -4.728,-11.391 0,-4.39 1.574,-8.162 4.728,-11.317 2.003,-2.096 6.449,-5.298 13.319,-9.594 C 16.044,11.419 24.252,5.831 29.223,0.86 c 8.021,-8.209 12.028,-18 12.028,-29.362 0,-11.561 -4.035,-21.397 -12.104,-29.514 -8.068,-8.114 -17.831,-12.176 -29.296,-12.176 -11.551,0 -21.37,4.062 -29.439,12.176 -8.068,8.117 -12.101,17.907 -12.101,29.372 l 18.045,4.728 c 0,-9.65 2.293,-16.745 6.88,-21.275 4.586,-4.54 10.118,-6.805 16.615,-6.805 6.402,0 11.916,2.265 16.548,6.805 4.633,4.53 6.946,10.099 6.946,16.689 0,6.496 -2.294,12.027 -6.871,16.613 C 13.797,-9.305 8.303,-5.346 0,0"
			/>
		</svg>
	);
}
